import { useState } from "react";
import { CheckCircle2, MessageSquare, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { QuoteView } from "./QuoteView";
import { Card, btn, btnOutline, inputCls, errMsg, fmtDate, money } from "./ui";

function OpenQuoteActions({ q, onUpdated }) {
  const [changes, setChanges] = useState(null);
  const call = async (path, body) => {
    try {
      onUpdated((await api.post(path, body)).data);
      toast.success(path.endsWith("accept") ? "Quotation accepted" : "Change request sent to DentiCision");
      setChanges(null);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <>
      <div className="flex flex-wrap gap-3">
        <button type="button" className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700 h-11 px-6`} data-testid="org-accept-quote" onClick={() => call(`/org/quotes/${q.id}/accept`)}>
          <CheckCircle2 className="w-4 h-4" /> Accept quotation
        </button>
        <button type="button" className={`${btnOutline} h-11 px-6`} data-testid="org-request-changes" onClick={() => setChanges(changes === null ? "" : null)}>
          <MessageSquare className="w-4 h-4" /> Request changes
        </button>
      </div>
      {changes !== null && (
        <div className="mt-3 flex gap-2">
          <input className={inputCls} placeholder="Describe the changes you need" value={changes} onChange={(e) => setChanges(e.target.value)} data-testid="org-changes-text" />
          <button type="button" className={btnOutline} data-testid="org-changes-submit" onClick={() => call(`/org/quotes/${q.id}/request-changes`, { text: changes })}>Send</button>
        </div>
      )}
      <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Accepting records your name, time and this exact version. Price and terms are locked after acceptance.</p>
    </>
  );
}

export function OrgQuotes({ org, onUpdated }) {
  const current = org.quotes.find((q) => ["sent", "changes_requested", "accepted"].includes(q.status));
  const history = org.quotes.filter((q) => q !== current);
  return (
    <div className="space-y-5" data-testid="org-quotes">
      {current ? (
        <QuoteView q={current} orgName={org.name}>
          {current.status === "sent" && <OpenQuoteActions q={current} onUpdated={onUpdated} />}
          {current.status === "changes_requested" && <p className="text-sm text-orange-700">Change request sent — DentiCision will send a revised quotation.</p>}
          {current.status === "accepted" && <p className="text-sm text-emerald-700">{current.complimentary ? "Accepted — DentiCision will activate your complimentary contract." : "Accepted — approved offer awaiting payment. Go to Billing to pay."}</p>}
        </QuoteView>
      ) : (
        <Card><p className="text-sm text-slate-600" data-testid="org-no-quote">No open quotation. DentiCision will prepare one for you — you will be notified by email.</p></Card>
      )}
      {history.length > 0 && (
        <Card>
          <p className="font-heading font-semibold text-[#0B2545]">Quotation history</p>
          <ul className="mt-3 divide-y divide-slate-100 text-sm">
            {history.map((q) => (
              <li key={q.id} className="py-2 flex justify-between gap-3"><span>{q.ref} v{q.version} · {q.licenses} licenses · {fmtDate(q.start)} → {fmtDate(q.end)}</span>
                <span className="text-slate-500 capitalize">{q.status} · {money(q.totals.total, q.currency)}</span></li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
